// Hand-written SDK for the custom, ANONYMOUS document re-upload mutations
// (validateReuploadToken / submitReupload). These live in UserMutations.Reupload.cs
// on the API and are NOT part of the generated CRUD SDK surface. Mirrors AuthSdk:
// constructor(private api) over GraphqlApiService and returns the raw
// {success,error,message,data} envelope for the caller to unwrap. No auth token is
// attached (the vendor opens the re-upload screen from a WhatsApp link, logged out).
import { Injectable } from '@angular/core';
import { GraphqlApiService } from '../../api/graphql-api.service';

export interface ReuploadEnvelope<T> {
  success: boolean;
  error?: string | null;
  message?: string | null;
  data?: T | null;
}

export interface ReuploadCheck {
  checkType: string;             // 'ssm' | 'ic' | 'bank_name_match'
  label: string;
  reason?: string | null;
  referenceValue?: string | null;
}

export interface ReuploadInfo {
  valid: boolean;
  vendorName?: string | null;
  rejected: ReuploadCheck[];
}

export interface ReuploadSubmitResult {
  ok: boolean;
  remaining: string[];           // checkTypes still rejected after this submit
}

/** One document the vendor re-submits. */
export interface ReuploadDoc {
  checkType: string;
  referenceValue?: string | null;
  fileName?: string | null;
  dataUrl: string;               // base64 data-URL (image/PDF)
}

@Injectable({ providedIn: 'root' })
export class ReuploadSdk {
  constructor(private api: GraphqlApiService) {}

  /** Resolve the token → which checks are rejected + why (valid:false when bad/expired). */
  async validate(token: string): Promise<ReuploadEnvelope<ReuploadInfo>> {
    const q = `mutation ValidateReupload($token:String!){
      validateReuploadToken(token:$token){
        success error message
        data { valid vendorName rejected { checkType label reason referenceValue } }
      }
    }`;
    const r = await this.api.request<{ validateReuploadToken: ReuploadEnvelope<ReuploadInfo> }>(q, { token });
    return r.validateReuploadToken;
  }

  /** Submit the re-uploaded documents (only currently-rejected checks are accepted). */
  async submit(token: string, docs: ReuploadDoc[]): Promise<ReuploadEnvelope<ReuploadSubmitResult>> {
    const q = `mutation SubmitReupload($token:String!,$docsJson:String!){
      submitReupload(token:$token,docsJson:$docsJson){
        success error message
        data { ok remaining }
      }
    }`;
    const r = await this.api.request<{ submitReupload: ReuploadEnvelope<ReuploadSubmitResult> }>(
      q, { token, docsJson: JSON.stringify(docs) });
    return r.submitReupload;
  }
}
